/**
 * RegistrationForm — the registration sheet, now a shell over the shared write.
 *
 * IT IS UNMOUNTED FROM THE SETUP CANVAS AND STILL MOUNTED ON INSIGHTS.
 * `ContractInsightDocuments` opens it, so it cannot be deleted; the setup page edits the same four
 * values in place, as rows. What must NEVER happen is two write paths for one
 * `vehicle_registrations` row — the storage path, the insert-then-point-the-vehicle-at-it order
 * and the "only overwrite the certificate when one was staged" rule would drift apart the first
 * time either surface was touched, and the two would then disagree about what a registration is.
 *
 * SO EVERYTHING BELOW THE FORM IS GONE. The read is `fetchVehicleRegistration`, the write is
 * `saveVehicleRegistration`, the validation is `validateRegistrationDraft`, and all three live in
 * `setup/data/` where the inline group calls them too. What is left here is the sheet: the fields,
 * the dirty guard on close, and the toasts an insights surface expects.
 */

import { useState, useMemo, useEffect } from 'react';
import { captureError } from '@/lib/logger';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import TransactionSelector from '@/components/ui/TransactionSelector';
import { FileUpload } from '@/components/ui/file-upload';
import {
    REGISTRATION_TYPES,
    REGISTRATION_TYPE_LABELS,
    type VehicleRegistrationType,
} from '@/lib/registration';
import {
    fetchVehicleRegistration,
    saveVehicleRegistration,
    vehicleRegistrationKey,
} from '@/components/contracts/setup/data/registration';

interface RegistrationFormProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    vehicleId: number;
    onSuccess?: () => void;
}

export default function RegistrationForm({
    open,
    onOpenChange,
    vehicleId,
    onSuccess,
}: RegistrationFormProps) {
    const queryClient = useQueryClient();
    const [type, setType] = useState<VehicleRegistrationType>('register');
    const [number, setNumber] = useState('');
    const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);
    const [certificateFile, setCertificateFile] = useState<File | null>(null);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    // THE SHARED READ, under the key the setup canvas uses — so a save on either surface
    // refreshes the other rather than leaving one of them showing a plate that has changed.
    const { data: existingData } = useQuery({
        queryKey: vehicleRegistrationKey(vehicleId),
        queryFn: () => fetchVehicleRegistration(vehicleId),
        enabled: open && !!vehicleId,
    });

    // Track original values for dirty checking
    const [originalValues, setOriginalValues] = useState<{
        type: string;
        number: string;
        transactionId: number | null;
    } | null>(null);

    // Prefill with existing data when it loads
    useEffect(() => {
        if (existingData) {
            const typeVal = (existingData.type as VehicleRegistrationType) || 'register';
            const numberVal = existingData.number || '';
            const txnVal = existingData.transaction_id || null;
            setType(typeVal);
            setNumber(numberVal);
            setSelectedTransactionId(txnVal);
            setOriginalValues({ type: typeVal, number: numberVal, transactionId: txnVal });
        } else if (open && !existingData) {
            setOriginalValues({ type: 'register', number: '', transactionId: null });
        }
    }, [existingData, open]);

    const isEditing = !!existingData;

    const isDirty = useMemo(() => {
        if (!originalValues) return false;
        return (
            type !== originalValues.type ||
            number !== originalValues.number ||
            selectedTransactionId !== originalValues.transactionId ||
            certificateFile !== null
        );
    }, [number, selectedTransactionId, type, originalValues, certificateFile]);

    const mutation = useMutation({
        mutationFn: () =>
            saveVehicleRegistration({
                vehicleId,
                registrationId: existingData?.id ?? null,
                draft: {
                    type,
                    number,
                    transactionId: selectedTransactionId == null ? '' : String(selectedTransactionId),
                },
                certificateFile,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            queryClient.invalidateQueries({ queryKey: vehicleRegistrationKey(vehicleId) });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            toast.success(isEditing ? 'Registration updated' : 'Registration added');
            onOpenChange(false);
            onSuccess?.();
        },
        // VERBATIM. `validateRegistrationDraft` names the field and the reason; paraphrasing it
        // into "Failed to save registration" is how an admin ends up retyping a form that was
        // never going to be accepted.
        onError: (error: Error) => {
            captureError(error);
            toast.error(error.message || 'Failed to save registration');
        },
    });

    const resetForm = () => {
        setType('register');
        setNumber('');
        setSelectedTransactionId(null);
        setCertificateFile(null);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        mutation.mutate();
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen && isDirty) {
            setShowCloseConfirm(true);
        } else {
            if (!newOpen) resetForm();
            onOpenChange(newOpen);
        }
    };

    const handleConfirmClose = () => {
        setShowCloseConfirm(false);
        resetForm();
        onOpenChange(false);
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleOpenChange}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !w-[35vw] !max-w-none rounded-2xl flex flex-col shadow-2xl border bg-background"
                >
                    <SheetHeader>
                        <SheetTitle>{isEditing ? 'Edit' : 'Add'} Vehicle Registration</SheetTitle>
                    </SheetHeader>

                    <form onSubmit={handleSubmit} className="flex-1 flex flex-col gap-6 mt-6">
                        <div className="flex-1 space-y-4 overflow-y-auto p-1">
                            <div className="space-y-2">
                                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Registration Type *</Label>
                                <Select value={type} onValueChange={(v) => setType(v as VehicleRegistrationType)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {REGISTRATION_TYPES.map((value) => (
                                            <SelectItem key={value} value={value}>
                                                {REGISTRATION_TYPE_LABELS[value]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    Type of vehicle registration process
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Registration Number *</Label>
                                <Input
                                    placeholder="e.g. GR-1234-24"
                                    value={number}
                                    onChange={(e) => setNumber(e.target.value.toUpperCase())}
                                    required
                                />
                                <p className="text-xs text-muted-foreground">
                                    Vehicle plate number
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Transaction *</Label>
                                <TransactionSelector
                                    value={selectedTransactionId}
                                    onChange={(id) => setSelectedTransactionId(id)}
                                    placeholder="Select a transaction..."
                                    defaultType="DEBIT"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Payment transaction for registration fees
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Registration Certificate</Label>
                                <FileUpload
                                    file={certificateFile}
                                    setFile={setCertificateFile}
                                    currentFileUrl={existingData?.certificate_url}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    maxSizeMB={5}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Upload the vehicle registration certificate document
                                </p>
                            </div>
                        </div>

                        <div className="pt-4 border-t flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={mutation.isPending}>
                                {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                {isEditing ? 'Save' : 'Add Registration'}
                            </Button>
                        </div>
                    </form>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard changes?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to close without saving?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Continue Editing</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmClose}>Discard</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
